package com.bulut.mecitogullari.core.export

import com.bulut.mecitogullari.core.export.ReportHtml.count
import com.bulut.mecitogullari.core.export.ReportHtml.escape
import com.bulut.mecitogullari.core.export.ReportHtml.money
import com.bulut.mecitogullari.core.i18n.Localization
import com.bulut.mecitogullari.core.i18n.unitDisplayLabel
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.core.util.formatDate
import com.bulut.mecitogullari.core.util.todayUtcMidnightMillis
import com.bulut.mecitogullari.data.model.ApartmentUnit
import com.bulut.mecitogullari.data.model.Craftsman
import com.bulut.mecitogullari.data.model.CraftsmanBalance
import com.bulut.mecitogullari.data.model.CraftsmanEntry
import com.bulut.mecitogullari.data.model.CraftsmanEntryType
import kotlin.math.abs

/** Everything needed to print one craftsman's account statement. */
data class CraftsmanReportData(
    val craftsman: Craftsman,
    val balance: CraftsmanBalance,
    val entries: List<CraftsmanEntry>,
    val unitsGiven: List<ApartmentUnit>,
)

/** Printable usta (iş karşılığı) hesap özeti — single craftsman or the whole list. */
object CraftsmanReportHtml {

    fun buildSingle(data: CraftsmanReportData, projectNames: Map<String, String>): String =
        ReportHtml.page(
            title = "${data.craftsman.name} - ${Localization.strings.pdfCraftsmanStatement}",
            body = buildString {
                appendHeader(data.craftsman)
                appendAccount(data, projectNames)
            },
        )

    fun buildAll(craftsmen: List<CraftsmanReportData>, projectNames: Map<String, String>): String =
        ReportHtml.page(
            title = Localization.strings.pdfCraftsmenOverview,
            body = buildString {
                append("<h1>${Localization.strings.pdfCraftsmenOverview}</h1>")
                append(
                    "<p class=\"subtitle\">${formatDate(todayUtcMidnightMillis())} • " +
                        Localization.strings.pdfCraftsmanCount
                            .fillTemplate("count" to craftsmen.size.toString()) + "</p>"
                )
                appendOverview(craftsmen)
                craftsmen.forEach { data ->
                    append("<div class=\"page-break\"></div>")
                    appendHeader(data.craftsman)
                    appendAccount(data, projectNames)
                }
            },
        )

    private fun StringBuilder.appendHeader(craftsman: Craftsman) {
        append("<h1>${escape(craftsman.name)}</h1>")
        append("<p class=\"subtitle\">${Localization.strings.pdfCraftsmanStatement} • ${formatDate(todayUtcMidnightMillis())}")
        if (craftsman.trade.isNotBlank()) append(" • ${escape(craftsman.trade)}")
        if (craftsman.phone.isNotBlank()) append(" • ${escape(craftsman.phone)}")
        append("</p>")
    }

    private fun StringBuilder.appendOverview(craftsmen: List<CraftsmanReportData>) {
        val s = Localization.strings
        append("<table>")
        append(
            "<thead><tr><th>${s.craftsman}</th><th>${s.pdfTrade}</th>" +
                "<th class=\"num\">${s.pdfDoneAgreed}</th>" +
                "<th class=\"num\">${s.pdfCurrentStatus}</th>" +
                "<th class=\"num\">${s.pdfWhenFinished}</th>" +
                "<th class=\"num\">${s.pdfRemainingInvoice}</th></tr></thead><tbody>"
        )
        craftsmen.forEach { data ->
            val craftsman = data.craftsman
            val balance = data.balance
            append("<tr>")
            append("<td>${escape(craftsman.name)}</td>")
            append("<td>${escape(craftsman.trade.ifBlank { s.none })}</td>")
            append(
                "<td class=\"num\">${count(craftsman.totalCompletedCount)} / " +
                    "${count(craftsman.totalAgreedCount)} ${s.units}</td>"
            )
            append("<td class=\"num\">${netText(balance.currentNet)}</td>")
            append("<td class=\"num\">${netText(balance.projectedNet)}</td>")
            append(
                "<td class=\"num\">${
                    if (balance.requiredInvoice > 0) money(balance.remainingInvoice) else s.none
                }</td>"
            )
            append("</tr>")
        }
        append("</tbody></table>")
    }

    private fun StringBuilder.appendAccount(data: CraftsmanReportData, projectNames: Map<String, String>) {
        val s = Localization.strings
        val craftsman = data.craftsman
        val balance = data.balance

        val workTypes = craftsman.effectiveWorkTypes.filter { it.agreedCount > 0 }
        if (workTypes.isNotEmpty()) {
            append("<h2>${s.pdfAgreedWork}</h2><table>")
            append(
                "<thead><tr><th>${s.pdfUnitType}</th><th class=\"num\">${s.pdfAgreed}</th>" +
                    "<th class=\"num\">${s.pdfDone}</th>" +
                    "<th class=\"num\">${s.pdfRemaining}</th>" +
                    "<th class=\"num\">${s.pdfUnitPrice}</th>" +
                    "<th class=\"num\">${s.pdfAgreedValue}</th>" +
                    "<th class=\"num\">${s.pdfEarned}</th></tr></thead><tbody>"
            )
            workTypes.forEach { workType ->
                append("<tr>")
                append("<td>${escape(workType.type)}</td>")
                append("<td class=\"num\">${count(workType.agreedCount)}</td>")
                append("<td class=\"num\">${count(workType.completedCount)}</td>")
                append("<td class=\"num\">${count(workType.remainingCount)}</td>")
                append("<td class=\"num\">${money(workType.unitPrice)}</td>")
                append("<td class=\"num\">${money(workType.agreedValue)}</td>")
                append("<td class=\"num credit\">${money(workType.earnedValue)}</td>")
                append("</tr>")
            }
            append("<tr class=\"total\">")
            append("<td>${s.total}</td>")
            append("<td class=\"num\">${count(craftsman.totalAgreedCount)}</td>")
            append("<td class=\"num\">${count(craftsman.totalCompletedCount)}</td>")
            append("<td class=\"num\">${count(craftsman.remainingWorkCount)}</td>")
            append("<td></td>")
            append("<td class=\"num\">${money(craftsman.agreedTotalValue)}</td>")
            append("<td class=\"num credit\">${money(craftsman.earnedFromWork)}</td>")
            append("</tr></tbody></table>")
        }

        if (data.unitsGiven.isNotEmpty()) {
            append("<h2>${s.pdfGivenUnits}</h2><table>")
            append(
                "<thead><tr><th>${s.project}</th><th>${s.pdfUnit}</th><th>${s.date}</th>" +
                    "<th class=\"num\">${s.pdfPrice}</th></tr></thead><tbody>"
            )
            data.unitsGiven.forEach { unit ->
                append("<tr>")
                append("<td>${escape(projectNames[unit.projectId] ?: s.none)}</td>")
                append("<td>${escape(unitDisplayLabel(unit.block, unit.number))}</td>")
                append("<td>${unit.saleDateEpochMillis?.let { formatDate(it) } ?: s.none}</td>")
                append("<td class=\"num\">${money(unit.salePrice ?: 0.0)}</td>")
                append("</tr>")
            }
            append(
                "<tr class=\"total\"><td colspan=\"3\">${s.total}</td>" +
                    "<td class=\"num\">${money(balance.unitsGivenValue)}</td></tr>"
            )
            append("</tbody></table>")
        }

        if (balance.requiredInvoice > 0 || balance.invoiced > 0) {
            append("<h2>${s.pdfInvoiceStatus}</h2><table>")
            append(
                "<tr><td>${s.pdfRequiredInvoice}</td>" +
                    "<td class=\"num\">${money(balance.requiredInvoice)}</td></tr>"
            )
            append("<tr><td>${s.pdfIssuedInvoice}</td><td class=\"num credit\">${money(balance.invoiced)}</td></tr>")
            append(
                "<tr class=\"total\"><td>${s.pdfRemainingInvoiceLong}</td>" +
                    "<td class=\"num debit\">${money(balance.remainingInvoice)}</td></tr>"
            )
            append("</table>")
        }

        if (data.entries.isNotEmpty()) {
            append("<h2>${s.pdfAccountEntries}</h2><table>")
            append(
                "<thead><tr><th>${s.date}</th><th>${s.description}</th><th>${s.pdfType}</th>" +
                    "<th class=\"num\">${s.amount}</th></tr></thead><tbody>"
            )
            data.entries.sortedBy { it.dateEpochMillis }.forEach { entry ->
                val (label, cssClass, sign) = when (entry.type) {
                    CraftsmanEntryType.EXTRA_WORK -> Triple(s.pdfExtraWork, "credit", "+")
                    CraftsmanEntryType.CASH_PAYMENT -> Triple(s.pdfCashPayment, "debit", "−")
                    CraftsmanEntryType.INVOICE -> Triple(s.pdfIssuedInvoiceEntry, "muted", "")
                }
                append("<tr>")
                append("<td>${formatDate(entry.dateEpochMillis)}</td>")
                append("<td>${escape(entry.description)}</td>")
                append("<td class=\"muted\">$label</td>")
                append("<td class=\"num $cssClass\">$sign${money(entry.amount)}</td>")
                append("</tr>")
            }
            append("</tbody></table>")
        }

        append("<table class=\"summary\">")
        append("<tr><td>${s.pdfEarnedFromWork}</td><td class=\"num credit\">${money(balance.earnedFromWork)}</td></tr>")
        if (balance.extras > 0) {
            append("<tr><td>${s.pdfExtraWork}</td><td class=\"num credit\">${money(balance.extras)}</td></tr>")
        }
        if (balance.unitsGivenValue > 0) {
            append("<tr><td>${s.pdfGivenUnitValue}</td><td class=\"num debit\">−${money(balance.unitsGivenValue)}</td></tr>")
        }
        if (balance.cashPaid > 0) {
            append("<tr><td>${s.pdfCashPaidTotal}</td><td class=\"num debit\">−${money(balance.cashPaid)}</td></tr>")
        }
        append("<tr class=\"total\"><td>${s.pdfCurrentStatus}</td><td class=\"num\">${netText(balance.currentNet)}</td></tr>")
        append(
            "<tr><td class=\"muted\">${s.pdfWhenFinished}</td>" +
                "<td class=\"num muted\">${netText(balance.projectedNet)}</td></tr>"
        )
        append("</table>")
    }

    /** Müteahhit gözünden: usta alacaklıysa kırmızı (ödenecek para), borçluysa yeşil. */
    private fun netText(net: Double): String {
        val s = Localization.strings
        return when {
            net > 0 -> "<span class=\"debit\">" +
                s.pdfCraftsmanOwedText.fillTemplate("amount" to money(net)) + "</span>"
            net < 0 -> "<span class=\"credit\">" +
                s.pdfCraftsmanOwesText.fillTemplate("amount" to money(abs(net))) + "</span>"
            else -> s.pdfAccountBalanced
        }
    }
}
